import { z } from 'zod';
import { MAX_AMOUNT } from './quantity.ts';
import { Category, CrateType, Item, Lot, LotPlace, Place, RecordId, Reference, StockLine } from './schemas.ts';

// The shapes of supply's calls for other modules (public.ts, ADR 0013, plan 2C).
// Amounts are in an item's base unit, as everywhere in supply (quantity.ts).

/** An item by id, or by name and category: found ignoring case among active items, or created (ensureItems). */
export const ItemRef = z.union([
  z.object({ id: RecordId }).strict(),
  z
    .object({
      name: z.string().trim().min(1).max(80),
      category: Category,
      /** For categories other than crates, materials and kits. */
      unit: z.enum(['scu', 'units']).optional(),
      crateType: CrateType.nullable().optional(),
    })
    .strict(),
]);
export type ItemRef = z.infer<typeof ItemRef>;

/** One line of stock from another module: a StockLine with its item. */
export const StockCallLine = StockLine.extend({ item: ItemRef });
export type StockCallLine = z.input<typeof StockCallLine>;

const Reason = z.string().trim().max(200).default('');

/** `stock`: up to 30 lines at one place, in one unit of work. */
export const StockCallInput = z.object({
  lines: z.array(StockCallLine).min(1).max(30),
  place: Place,
  reason: Reason,
  ref: Reference.nullable().default(null),
});
export type StockCallInput = z.input<typeof StockCallInput>;

/** What a call added to or took from one lot. */
export const LotAmount = z.object({ lotId: z.string(), item: z.string(), amount: z.number().int() });
export type LotAmount = z.infer<typeof LotAmount>;

/** What each lot gained, and each input line's lot in order (null for a line of 0), so a caller can undo line by line. */
export const StockCallResult = z.object({ stocked: z.array(LotAmount), lineLots: z.array(z.string().nullable()) });
export type StockCallResult = z.infer<typeof StockCallResult>;

const TakeInput = z.object({ lotId: RecordId, amount: z.number().int().min(1).max(MAX_AMOUNT) });

/**
 * `deduct`: takes amounts out of lots. Refused if any lot holds less, unless
 * `partial`, which takes whatever is still there (a lot that's gone gives 0), for
 * undoing a donation (plan 2C R9).
 */
export const DeductCallInput = z.object({
  takes: z.array(TakeInput).min(1).max(30),
  reason: Reason,
  ref: Reference.nullable().default(null),
  partial: z.boolean().default(false),
});
export type DeductCallInput = z.input<typeof DeductCallInput>;

/** What was taken from each lot asked for, and what was wanted: less only with `partial`. */
export const DeductCallResult = z.object({ taken: z.array(LotAmount.extend({ wanted: z.number().int() })) });
export type DeductCallResult = z.infer<typeof DeductCallResult>;

/** `giveBack`: puts amounts back onto their lots. */
export const GiveBackCallInput = z.object({ takes: z.array(TakeInput).min(1).max(30), reason: Reason, ref: Reference.nullable().default(null) });
export type GiveBackCallInput = z.input<typeof GiveBackCallInput>;

export const GiveBackCallResult = z.object({ returned: z.array(LotAmount) });
export type GiveBackCallResult = z.infer<typeof GiveBackCallResult>;

/** `findItems`: active items whose names contain `query`, or are one of `names` (ignoring case), with their lots that hold anything. */
export const FindItemsInput = z.object({
  query: z.string().trim().max(80).default(''),
  names: z.array(z.string().trim().min(1).max(80)).max(60).default([]),
  limit: z.number().int().min(1).max(100).default(20),
});
export type FindItemsInput = z.input<typeof FindItemsInput>;

export const FindItemsResult = z.object({ items: z.array(Item), lots: z.array(Lot) });
export type FindItemsResult = z.infer<typeof FindItemsResult>;

/** `lots`: lots by id, with their items, for a caller that planned against them. A lot that's gone isn't returned. */
export const LotsInput = z.object({ ids: z.array(RecordId).min(1).max(60) });
export type LotsInput = z.input<typeof LotsInput>;

export const LotsResult = z.object({ lots: z.array(Lot), items: z.array(Item) });
export type LotsResult = z.infer<typeof LotsResult>;

/** `refineryRuns`: org jobs picked up since a time, per refiner. Personal jobs stay private. */
export const RefineryRunsInput = z.object({ since: z.iso.datetime().nullable().default(null) });
export type RefineryRunsInput = z.input<typeof RefineryRunsInput>;

export const RefineryRunsResult = z.object({ runs: z.array(z.object({ refiner: z.string(), count: z.number().int(), scu: z.number().int() })) });
export type RefineryRunsResult = z.infer<typeof RefineryRunsResult>;

/** `readiness`: stocked ships at their prep level, and how many fall short (plan 2E R14). */
export const ReadinessResult = z.object({ ready: z.number().int(), short: z.number().int(), total: z.number().int() });
export type ReadinessResult = z.infer<typeof ReadinessResult>;

/** `shipOwners`: active ships per owner, a member or a guest's name. */
export const ShipOwnersResult = z.object({ owners: z.array(z.object({ userId: z.string().nullable(), name: z.string().nullable(), ships: z.array(z.string()) })) });
export type ShipOwnersResult = z.infer<typeof ShipOwnersResult>;

/** `lowStock`: stockpile prep levels that are low (plan 2B R18), as the Supply page has them. */
export const LowStockResult = z.object({ low: z.array(z.object({ item: Item, place: LotPlace, have: z.number().int(), target: z.number().int() })) });
export type LowStockResult = z.infer<typeof LowStockResult>;

/** A stocked, active ship as a place to put things, with its capacity in thousandths of an SCU (null unset) and what's aboard. */
export const PlaceShip = z.object({ id: z.string(), name: z.string(), hull: z.string(), cargoCapacity: z.number().int().nullable(), used: z.number().int() });
export type PlaceShip = z.infer<typeof PlaceShip>;

/** `places`: the stockpile locations in use, and the ships stock can go aboard. */
export const PlacesResult = z.object({ locations: z.array(z.string()), ships: z.array(PlaceShip) });
export type PlacesResult = z.infer<typeof PlacesResult>;

/** `shipResources`: the battle resources aboard ships (plan 2B R10), for 2D. */
export const ShipResourcesInput = z.object({ shipIds: z.array(RecordId).min(1).max(50) });
export type ShipResourcesInput = z.input<typeof ShipResourcesInput>;

export const ShipResourcesResult = z.object({ ships: z.record(z.string(), z.record(z.string(), z.object({ amount: z.number().int(), variance: z.number().int() }))) });
export type ShipResourcesResult = z.infer<typeof ShipResourcesResult>;

/** `supplyReport`: the supply report's text (R19), as the Supply page copies it: for a request post's "full supply report" (plan 2C R14). */
export const SupplyReportResult = z.object({ text: z.string() });
export type SupplyReportResult = z.infer<typeof SupplyReportResult>;
